import React, { useCallback, useMemo } from 'react';
import {
  CheckboxField,
  TextField,
  TextareaField,
  Tooltip,
  SelectField,
} from '@wk/components-v3-react16';
import { useAtom } from 'jotai';
import styles from './StepPropertiesForm.module.scss';
import {
  Parameter,
  ServerParameterGroup,
  ServerStep,
} from '../../../../ServerWorkflowType';
import {
  validatedRetryDelayAtom,
  validatedRetryDelayMultiplierAtom,
  validatedRetryNumberAtom,
  validatedStepConditionAtom,
  validatedStepIdAtom,
  validatedStepTitleAtom,
} from '../../../../validatedStepFieldAtoms';
import {
  createParameterEscSpecSymbolsAtom,
  createParameterIsDynamicAtom,
  createParameterValueAtom,
} from '../../../../stepFieldAtoms';
import { useValidatedField } from '../../../../formHooks';

const ERROR_STYLE = {
  color: 'var(--color-error, #d32f2f)',
  fontSize: '12px',
};

interface StepPropertiesFormProperties {
  step: ServerStep;
}

const StepPropertiesForm: React.FC<StepPropertiesFormProperties> = ({
  step,
}) => {
  const {
    value: stepTitle,
    handleChange: handleStepTitleChange,
    handleBlur: handleStepTitleBlur,
    showError: showStepTitleError,
    error: stepTitleError,
  } = useValidatedField(validatedStepTitleAtom);
  const {
    value: stepId,
    handleChange: handleStepIdChange,
    handleBlur: handleStepIdBlur,
    showError: showStepIdError,
    error: stepIdError,
  } = useValidatedField(validatedStepIdAtom);

  const {
    value: stepCondition,
    handleChange: handleStepConditionChange,
    handleBlur: handleStepConditionBlur,
    showError: showStepConditionError,
    error: stepConditionError,
  } = useValidatedField(validatedStepConditionAtom);

  const {
    value: retryNumber,
    handleChange: handleRetryNumberChange,
    handleBlur: handleRetryNumberBlur,
    showError: showRetryNumberError,
    error: retryNumberError,
  } = useValidatedField(validatedRetryNumberAtom);

  const {
    value: retryDelay,
    handleChange: handleRetryDelayChange,
    handleBlur: handleRetryDelayBlur,
    showError: showRetryDelayError,
    error: retryDelayError,
  } = useValidatedField(validatedRetryDelayAtom);

  const {
    value: retryDelayMultiplier,
    handleChange: handleRetryDelayMultiplierChange,
    handleBlur: handleRetryDelayMultiplierBlur,
    showError: showRetryDelayMultiplierError,
    error: retryDelayMultiplierError,
  } = useValidatedField(validatedRetryDelayMultiplierAtom);

  const onStepTitleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleStepTitleChange(event.target.value);
    },
    [handleStepTitleChange]
  );

  const onStepIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleStepIdChange(event.target.value);
    },
    [handleStepIdChange]
  );

  const onStepConditionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleStepConditionChange(event.target.value);
    },
    [handleStepConditionChange]
  );

  const onRetryNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleRetryNumberChange(Number(event.target.value));
    },
    [handleRetryNumberChange]
  );

  const onRetryDelayChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleRetryDelayChange(Number(event.target.value));
    },
    [handleRetryDelayChange]
  );

  const onRetryDelayMultiplierChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleRetryDelayMultiplierChange(Number(event.target.value));
    },
    [handleRetryDelayMultiplierChange]
  );

  return (
    <div className={styles.sidePanel}>
      <h1>General Step Properties</h1>
      <div className={styles.formWrapper}>
        <div className={styles.fieldRow}>
          <TextField label="Step Title">
            <input
              onBlur={handleStepTitleBlur}
              onChange={onStepTitleChange}
              placeholder="Enter step title"
              type="text"
              value={stepTitle || ''}
            />
          </TextField>
          {showStepTitleError && stepTitleError && (
            <span style={ERROR_STYLE}>{stepTitleError}</span>
          )}
        </div>

        <div className={styles.fieldRow}>
          <TextField label="Step ID">
            <input
              onBlur={handleStepIdBlur}
              onChange={onStepIdChange}
              placeholder="Enter step ID"
              type="text"
              value={stepId || ''}
            />
          </TextField>
          {showStepIdError && stepIdError && (
            <span style={ERROR_STYLE}>{stepIdError}</span>
          )}
        </div>

        <div className={styles.fieldRow}>
          <TextField label="Step Condition">
            <input
              onBlur={handleStepConditionBlur}
              onChange={onStepConditionChange}
              placeholder="Enter step condition"
              type="text"
              value={stepCondition || ''}
            />
          </TextField>
          {showStepConditionError && stepConditionError && (
            <span style={ERROR_STYLE}>{stepConditionError}</span>
          )}
        </div>

        <div className={styles.fieldRow}>
          <TextField label="Retry Number">
            <input
              min={0}
              onBlur={handleRetryNumberBlur}
              onChange={onRetryNumberChange}
              placeholder="Number of retries"
              type="number"
              value={retryNumber ?? ''}
            />
          </TextField>
          {showRetryNumberError && retryNumberError && (
            <span style={ERROR_STYLE}>{retryNumberError}</span>
          )}
        </div>

        <div className={styles.fieldRow}>
          <TextField label="Retry Delay (ms)">
            <input
              min={0}
              onBlur={handleRetryDelayBlur}
              onChange={onRetryDelayChange}
              placeholder="Delay in milliseconds"
              type="number"
              value={retryDelay ?? ''}
            />
          </TextField>
          {showRetryDelayError && retryDelayError && (
            <span style={ERROR_STYLE}>{retryDelayError}</span>
          )}
        </div>

        <div className={styles.fieldRow}>
          <TextField label="Retry Delay Multiplier">
            <input
              min={0}
              onBlur={handleRetryDelayMultiplierBlur}
              onChange={onRetryDelayMultiplierChange}
              placeholder="Multiplier for successive retries"
              step={0.1}
              type="number"
              value={retryDelayMultiplier ?? ''}
            />
          </TextField>
          {showRetryDelayMultiplierError && retryDelayMultiplierError && (
            <span style={ERROR_STYLE}>{retryDelayMultiplierError}</span>
          )}
        </div>
      </div>

      <h1>Additional Step Properties</h1>
      <div className={styles.formWrapper}>
        {step.paramGroups.map(
          (parameterGroup: ServerParameterGroup, groupIndex: number) => {
            return parameterGroup.parameters.map(
              (parameter: Parameter, parameterIndex: number) => (
                <ParameterField
                  key={`${groupIndex}-${parameterIndex}-${parameter.parameter.textId}`}
                  groupIndex={groupIndex}
                  parameter={parameter}
                  parameterIndex={parameterIndex}
                  step={step}
                />
              )
            );
          }
        )}
      </div>
    </div>
  );
};

interface ParameterFieldProperties {
  groupIndex: number;
  parameter: Parameter;
  parameterIndex: number;
  step: ServerStep;
}

const ParameterField: React.FC<ParameterFieldProperties> = ({
  groupIndex,
  parameter,
  parameterIndex,
  step,
}) => {
  const { parameter: parameterDefinition } = parameter;
  const { type, textId, required, values } = parameterDefinition;
  const requiredIndicator = required ? 'required' : 'none';
  // Find the full parameter definition from the module
  const moduleParameter = step.module.parameters.find(
    (p) => p.textId === textId
  );

  const valueAtom = useMemo(
    () => createParameterValueAtom(groupIndex, parameterIndex),
    [groupIndex, parameterIndex]
  );
  const isDynamicAtom = useMemo(
    () => createParameterIsDynamicAtom(groupIndex, parameterIndex),
    [groupIndex, parameterIndex]
  );
  const escSpecSymbolsAtom = useMemo(
    () => createParameterEscSpecSymbolsAtom(groupIndex, parameterIndex),
    [groupIndex, parameterIndex]
  );

  const [value, setValue] = useAtom(valueAtom);
  const [isDynamicValue, setIsDynamicValue] = useAtom(isDynamicAtom);
  const [escSpecSymbolsValue, setEscSpecSymbolsValue] =
    useAtom(escSpecSymbolsAtom);

  const handleValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
    },
    [setValue]
  );

  const handleSelectChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setValue(event.target.value);
    },
    [setValue]
  );

  const handleTextareaChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(event.target.value);
    },
    [setValue]
  );

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.checked.toString());
    },
    [setValue]
  );

  const handleIsDynamicChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsDynamicValue(event.target.checked);
    },
    [setIsDynamicValue]
  );

  const handleEscSpecSymbolsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEscSpecSymbolsValue(event.target.checked);
    },
    [setEscSpecSymbolsValue]
  );

  const labelText = moduleParameter?.name || textId;
  const tooltipText = moduleParameter?.tooltip || undefined;

  const renderParameterInput = () => {
    switch (type) {
      case 'INTEGER': {
        return (
          <TextField label={labelText} indicator={requiredIndicator}>
            <input
              onChange={handleValueChange}
              placeholder={`Enter ${labelText.toLowerCase()}`}
              title={tooltipText}
              type="number"
              value={value || ''}
              required={required}
              aria-required={required}
            />
          </TextField>
        );
      }

      case 'BOOLEAN': {
        const fieldId = `param-${textId}`;
        return (
          <CheckboxField label={labelText} indicator={requiredIndicator}>
            <input
              checked={value === 'true'}
              flow-id="checkbox"
              id={fieldId}
              onChange={handleCheckboxChange}
              title={tooltipText}
              type="checkbox"
              required={required}
              aria-required={required}
            />
          </CheckboxField>
        );
      }

      case 'SECURE': {
        return (
          <TextField label={labelText} indicator={requiredIndicator}>
            <input
              onChange={handleValueChange}
              placeholder={`Enter ${labelText.toLowerCase()}`}
              title={tooltipText}
              type="password"
              value={value || ''}
              required={required}
              aria-required={required}
            />
          </TextField>
        );
      }
      case 'ENUM': {
        return (
          <SelectField
            label={labelText}
            labelFor="select"
            labelId={`select-${labelText}`}
          >
            <select
              id={`select-${labelText}`}
              flow-id={`select-${labelText}`}
              onChange={handleSelectChange}
              value={value || ''}
            >
              {values?.map((val) => (
                <option id={val} key={val} value={val}>
                  {val}
                </option>
              ))}
            </select>
          </SelectField>
        );
      }

      default: {
        const shouldUseTextarea = (value?.length || 0) > 100;

        if (shouldUseTextarea) {
          return (
            <TextareaField label={labelText} indicator={requiredIndicator}>
              <textarea
                onChange={handleTextareaChange}
                placeholder={`Enter ${labelText.toLowerCase()}`}
                rows={4}
                title={tooltipText}
                value={value || ''}
                required={required}
                aria-required={required}
              />
            </TextareaField>
          );
        }

        return (
          <TextField label={labelText} indicator={requiredIndicator}>
            <input
              onChange={handleValueChange}
              placeholder={`Enter ${labelText.toLowerCase()}`}
              title={tooltipText}
              type="text"
              value={value || ''}
              required={required}
              aria-required={required}
            />
          </TextField>
        );
      }
    }
  };

  return (
    <div className={styles.parameterFieldWrapper}>
      <div className={styles.parameterInputWrapper}>
        {renderParameterInput()}
      </div>
      <div className={styles.parameterCheckboxes}>
        <CheckboxField>
          <input
            checked={isDynamicValue}
            flow-id="checkbox"
            id={`isDynamic-${groupIndex}-${parameterIndex}`}
            onChange={handleIsDynamicChange}
            type="checkbox"
          />
        </CheckboxField>
        <CheckboxField>
          <input
            checked={escSpecSymbolsValue}
            flow-id="checkbox"
            id={`escSpecSymbols-${groupIndex}-${parameterIndex}`}
            onChange={handleEscSpecSymbolsChange}
            type="checkbox"
          />
        </CheckboxField>
        <Tooltip
          targetSelector={`#isDynamic-${groupIndex}-${parameterIndex}`}
          elementId="tooltip"
        >
          {' '}
          Is Dynamic{' '}
        </Tooltip>
        <Tooltip
          targetSelector={`#escSpecSymbols-${groupIndex}-${parameterIndex}`}
          elementId="tooltip"
        >
          {' '}
          Escape Special Symbols{' '}
        </Tooltip>
      </div>
    </div>
  );
};

export default StepPropertiesForm;
